import UnivoLink from "@/components/common/UnivoLink";
import UnivoImage from "@/components/common/UnivoImage";
import styles from "./ProgramCard.module.scss";
import DurationIcon from "@/SvgIcons/DurationIcon";
import clsx from "clsx";
import { memo } from "react";

const SPONSORED_LABELS: Record<string, string> = {
  RICS: "In Association with",
  WOMEN: "Exclusive Cohort",
};
const DEFAULT_SPONSORED_LABEL = "In Collaboration with";

export interface ProgramCardProps {
  image?: string;
  partnerName?: string;
  title: string;
  description?: string;
  tags?: string[];
  duration?: string;
  fee?: string;
  href?: string;
  courseStripText?: string;
  ShortSponsoredImage?: string;
  SponsoredKey?: string;
  /** Opt-in LCP for above-the-fold cards (first row). Default lazy. */
  priority?: boolean;
}

const ProgramCard = ({
  image,
  partnerName,
  title,
  description,
  tags = [],
  duration,
  fee,
  href = "#",
  courseStripText,
  ShortSponsoredImage,
  SponsoredKey,
  priority = false,
}: ProgramCardProps) => {
  const PMI_PROGRAM_PATHS = [
  "/bba-in-project-management",
  "/bca-in-project-management",
  "/mca-in-project-management",
  "/mba-in-project-management",
];

const isPMIProgram = PMI_PROGRAM_PATHS.includes(href);
  return (
    <UnivoLink href={href} className={styles.card} prefetch={false}>
      <div className={styles.imageArea}>
        {image ? (
          <UnivoImage
            src={image}
            alt={title}
            fill
            sizes="(max-width: 1023px) 100vw, 460px"
            className={styles.image}
            priority={priority}
            isLcp={priority}
            loading={priority ? "eager" : "lazy"}
          />
        ) : (
          <div className={styles.imagePlaceholder} />
        )}

        {partnerName && (
          <div className={styles.partnerBadge}>
            <span className={styles.partnerLabel}>In Collaboration with</span>
            <span className={styles.partnerName}>{partnerName}</span>
          </div>
        )}

        {courseStripText && (
          <div className={styles.qsBadgeWrap}>
            <span className={styles.qsBadge}>{courseStripText}</span>
            <span className={styles.qsArrow} aria-hidden="true" />
          </div>
        )}

        {ShortSponsoredImage && (
          <div className={styles.CollaborationBadgeWrap}>
            {!isPMIProgram && <p className={styles.CollaborationBadge}>
              {SPONSORED_LABELS[SponsoredKey ?? ""] ?? DEFAULT_SPONSORED_LABEL}
            </p>}
            <UnivoImage
              src={ShortSponsoredImage}
              alt="Amity programs"
              className={styles.Collaborationimg}
              width={120}
              height={29}
              sizes="120px"
              loading="lazy"
              fetchPriority="low"
            />
          </div>
        )}
      </div>

      {/* ── Content area ── */}
      <div className={styles.content}>
        <div className={styles.info}>
          <h3 className={styles.title}>{title}</h3>
          {description && (
            <p title={description} className={styles.description}>
              {description}
            </p>
          )}
        </div>

        {tags.length > 0 && (
          <div className={styles.tags}>
            {tags.slice(0, 5).map((tag) => (
              <span key={tag} className={styles.tag}>
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className={styles.footer}>
          {isPMIProgram && (
            <p className={styles.startingPriceLabel}>
              Starting From
            </p>
          )}
          <div className={styles.footerInnerContainer}>
          {duration && (
            <div className={styles.footerItem}>
              <DurationIcon size={20} />
              <span className={clsx(styles.footerDuration, "lining-nums")}>
                {duration}
              </span>
            </div>
          )}
          {fee && (
            <span className={clsx(styles.footerFee, "lining-nums")}>{fee}</span>
          )}
          </div>
        </div>
      </div>
    </UnivoLink>
  );
};

export default memo(ProgramCard);
